import {
  BufferAttribute,
  CatmullRomCurve3,
  Group,
  IcosahedronGeometry,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Vector3,
  TubeGeometry,
  CylinderGeometry,
  Quaternion,
} from 'three'
import type { BufferGeometry } from 'three'
import { BufferGeometryUtils } from 'three/examples/jsm/Addons'
import type { DrillDataConfig, DrillDataSet, DrillHoleApiData, DrillHoles, DrillLegendViewModel, DrillLegend } from '~/entities/drillHole'
import type { ApiListView } from '~/entities/api'
import { disposeScene } from '~/lib/three-d-viewer/utils'

export class DrillHoleManager {
  public group = new Group()

  private holes: Map<number, DrillHoles> = new Map()
  private sceneConfigs: DrillDataConfig[]
  private surveyCurves: Map<string, CatmullRomCurve3> = new Map()
  private assayMap: Map<number, Mesh[]> = new Map()
  private collarMesh: Mesh | null = null
  private loaderConfig: { uuid: string | null }
  private onLabelsChanged: () => void
  private modelId: number | null = null
  private registeredLegends: Map<string | number, DrillLegendViewModel>

  constructor(loaderConfig: { uuid: string | null }, onLabelsChanged: () => void) {
    this.loaderConfig = loaderConfig
    this.group.name = 'Drill Holes'
    this.sceneConfigs = []
    this.registeredLegends = new Map()
    this.onLabelsChanged = onLabelsChanged
  }

  async fetchHoles({ modelId, drillDataSets }: { modelId: number; drillDataSets: DrillDataSet[] }) {
    this.modelId = modelId

    if (this.modelId !== modelId) return // avoid race condition

    this.holes = await this._getDrillHoleMap(drillDataSets)
  }

  async addHolesForDataSet(drillDataSets: DrillDataSet[]) {
    this.holes = await this._getDrillHoleMap(drillDataSets)

    const defaultConfigs = Array.from(this.holes.values()).flatMap(hole => hole.config)
    const defaultLegends = this._generateLegends(defaultConfigs)

    this._setSelectedLegendVisible(defaultLegends)
    this.registeredLegends = defaultLegends
    this.onLabelsChanged()
    this._createAllDrillHoles()
  }

  getHolesForDrillDataSet(drillDataSetId: number): DrillHoles | undefined {
    return this.holes.get(drillDataSetId)
  }

  removeDrillHolesForDataSet(drillDataSetId: number) {
    this.holes.delete(drillDataSetId)
    this._createAllDrillHoles()
  }

  isLegendVisible(legendId: string | number): boolean {
    const legend = this.registeredLegends.get(legendId)
    return legend?.visible ?? false
  }

  setVisible(legendId: string | number, visible: boolean) {
    const legend = this.registeredLegends.get(legendId)

    if (!legend) return

    this.registeredLegends.set(legendId, { ...legend, visible })
  }

  setActiveLegend(legendId: string | number) {
    const legend = this.registeredLegends.get(legendId)

    if (!legend) return

    this._deselectLegendsForDataSet(legend.dataSetId)
    this.registeredLegends.set(legendId, { ...legend, isSelected: true, visible: true })
    this.onLabelsChanged()
    this._createAllAssays(legend.dataSetId)
  }

  getLegendList(): DrillLegendViewModel[] {
    return Array.from(this.registeredLegends.values())
  }

  getLegendById(legendId: string | number): DrillLegendViewModel | undefined {
    return this.registeredLegends.get(legendId)
  }

  getSelectedResourceForDataSet(dataSetId: number): string | undefined {
    const legend = Array.from(this.registeredLegends.values()).find(legend => {
      return legend.dataSetId === dataSetId && legend.isSelected
    })
    return legend?.original.name
  }

  removeLegendForDataSet(dataSetId: number) {
    this.registeredLegends.forEach((legend, id) => {
      if (legend.dataSetId === dataSetId) {
        this.registeredLegends.delete(id)
      }
    })
  }

  async setLegendsForScene(sceneId: number | null) {
    if (!sceneId) return

    this.sceneConfigs = []
    // TODO: handle caching
    const configs = await $fetch<DrillDataConfig[]>(`/v2/3dmodel/cameras/${sceneId}/drill-configurations`, {
      ...useFetchSharedOptions(),
      headers: {
        ...useFetchSharedOptions().headers,
        'X-Deck-Auth-UUID': `${this.loaderConfig.uuid}`,
      },
    })

    const defaultConfigs = Array.from(this.holes.values()).flatMap(hole => hole.config)

    if (!configs.length) {
      const defaultLegends = this._generateLegends(defaultConfigs)

      this.registeredLegends = defaultLegends
      this.onLabelsChanged()
      this._createAllDrillHoles()

      return
    }

    this._overrideDataSetLegends(configs, defaultConfigs)
    this.onLabelsChanged()
    this.sceneConfigs = configs
    this._createAllDrillHoles()
  }

  async updateDrillHoleLegends(sceneId: number) {
    const defaultConfigs = Array.from(this.holes.values()).flatMap(x => x.config)

    const configsToAdd = defaultConfigs.filter(
      x => !this.sceneConfigs.some((y: DrillDataConfig) => y.data_set_id === x.data_set_id && sceneId === y?.camera_id),
    )

    if (configsToAdd.length) {
      await $fetch(`/v2/3dmodel/cameras/${sceneId}/drill-configurations`, {
        method: 'POST',
        ...useFetchSharedOptions(),
        headers: {
          ...useFetchSharedOptions().headers,
          'X-Deck-Auth-UUID': `${this.loaderConfig.uuid}`,
        },
        body: JSON.stringify(this._getUpdatedLegends(configsToAdd).map(({ id, ...rest }) => rest)),
      })

      return
    }

    const configsToDelete = this.sceneConfigs.filter(
      x => !x.numerical_assay?.some(y => this.registeredLegends.has(`${(y as DrillLegendViewModel).dataSetId}-${y.id}`)),
    )

    if (configsToDelete.length) {
      configsToDelete.forEach(async config => {
        await $fetch(`/v2/3dmodel/cameras/${sceneId}/drill-configurations/${config.id}`, {
          method: 'DELETE',
          ...useFetchSharedOptions(),
          headers: {
            ...useFetchSharedOptions().headers,
            'X-Deck-Auth-UUID': `${this.loaderConfig.uuid}`,
          },
          body: JSON.stringify(this._getUpdatedLegends(configsToDelete)),
        })
      })
    }

    const numericalAssays = this.sceneConfigs.flatMap(x => x.numerical_assay)
    const hasUpdates = numericalAssays.some(x => x && this.registeredLegends.has(`${(x as DrillLegendViewModel).dataSetId}-${x.id}`))
    const configsToUpdate = this.sceneConfigs.filter(x =>
      x.numerical_assay?.some(y => this.registeredLegends.has(`${(y as DrillLegendViewModel).dataSetId}-${y.id}`)),
    )

    if (hasUpdates) {
      await $fetch(`/v2/3dmodel/cameras/${sceneId}/drill-configurations`, {
        method: 'PATCH',
        ...useFetchSharedOptions(),
        headers: {
          ...useFetchSharedOptions().headers,
          'X-Deck-Auth-UUID': `${this.loaderConfig.uuid}`,
        },
        body: JSON.stringify(this._getUpdatedLegends(configsToUpdate)),
      })

      return
    }
  }

  private _getUpdatedLegends(config: DrillDataConfig[] = this.sceneConfigs) {
    const assays = config.flatMap(x => x)

    return assays.map(item => {
      const dataSetId = item.data_set_id
      const res: typeof item = { ...item }

      this.registeredLegends.forEach((registeredLegend, id) => {
        if (registeredLegend.dataSetId !== dataSetId) return

        const legendId = (id as string).split('-')[1]
        const assay = res[registeredLegend.type]

        if (!Array.isArray(assay)) return

        res[registeredLegend.type] = assay.map(legend => {
          return legend.id === legendId ? { ...legend, ...registeredLegend, id: legendId } : legend
        })
      })

      return res
    })
  }

  private _deselectLegendsForDataSet(dataSetId: number) {
    this.registeredLegends.forEach((legend, id) => {
      if (legend.dataSetId === dataSetId) {
        this.registeredLegends.set(id, { ...legend, isSelected: false, visible: false })
      }
    })
  }

  private _overrideDataSetLegends(configs: DrillDataConfig[], oldConfigs: DrillDataConfig[]): void {
    const oldLegends = this._generateLegends(oldConfigs)
    const newLegends = this._generateLegends(configs)
    this.registeredLegends = new Map([...oldLegends, ...newLegends])
  }

  private _generateLegends(configs: DrillDataConfig[]): Map<string, DrillLegendViewModel> {
    const newLegends = new Map<string, DrillLegendViewModel>()

    configs.forEach(config => {
      const { data_set_id: dataSetId, numerical_assay } = config

      let selectedIndex = numerical_assay?.findIndex(
        (assay: DrillLegendViewModel | DrillLegend) => (assay as DrillLegendViewModel).isSelected,
      )

      if (selectedIndex === -1) {
        selectedIndex = 0
      }

      numerical_assay?.forEach((assay: DrillLegendViewModel | DrillLegend, index: number) => {
        const id = `${dataSetId}-${assay.id}`

        newLegends.set(id, {
          ...assay,
          id,
          type: 'numerical_assay',
          dataSetId,
          visible: (assay as DrillLegendViewModel).visible ?? false,
          isSelected: index === selectedIndex,
        })
      })
    })

    return newLegends
  }

  private _setSelectedLegendVisible(map: Map<string, DrillLegendViewModel>) {
    map.forEach(value => {
      if (value.isSelected) {
        value.visible = true
      } else {
        value.visible = false
      }
    })
  }

  private _createAllCollars(drillHoles: DrillHoles, localizationOffset: Vector3) {
    const geoArray = []
    const matrix = new Matrix4()

    for (let i = 0; i < drillHoles.data.length; i++) {
      const drillHole = drillHoles.data[i]
      const collar = drillHole.collar
      const collarGeo = new IcosahedronGeometry(3)

      const position = new Vector3(collar?.point.x, collar?.point.y, collar?.point.z).sub(localizationOffset)
      const vertexCount = collarGeo.attributes.position.count

      const origin = new Float32Array(vertexCount * 3) // 3 floats per vertex
      for (let i = 0; i < vertexCount; i++) {
        origin.set([position.x, position.y, position.z], i * 3)
      }

      collarGeo.setAttribute('origin', new BufferAttribute(origin, 3))

      matrix.setPosition(position)
      collarGeo.applyMatrix4(matrix)
      geoArray.push(collarGeo)
    }

    const geo = BufferGeometryUtils.mergeGeometries(geoArray)
    this.collarMesh = new Mesh(geo, new MeshStandardMaterial({ color: 0xffffff }))
    this.group.add(this.collarMesh)
  }

  private _createAllSurveys(drillHoles: DrillHoles, localizationOffset: Vector3) {
    const instanceCount = drillHoles.data.length

    for (let i = 0; i < instanceCount; i++) {
      const drillHole = drillHoles.data[i]
      const points: Vector3[] = []
      const mat = new MeshBasicMaterial({ color: 0xe6e6e6 })
      if (!drillHole.survey?.points) continue
      for (let j = 0; j < drillHole.survey.points.length; j++) {
        const survey = drillHole.survey

        const pos = new Vector3(survey.points[j].x, survey.points[j].y, survey.points[j].z).sub(localizationOffset)
        points.push(pos)
      }
      const curve = new CatmullRomCurve3(points)
      const geo = new TubeGeometry(curve, 15, 1, 5, false)

      const mesh = new Mesh(geo, mat)

      this.surveyCurves.set(drillHole.hole_id, curve)
      this.group.add(mesh)
    }
  }

  private _createAllAssays(dataSetId: number) {
    const drillHoles = this.holes.get(dataSetId)
    if (!drillHoles) return

    let resourceIndex: number | undefined
    const resourceName = this.getSelectedResourceForDataSet(dataSetId)
    if (resourceName) {
      resourceIndex = drillHoles.data[0].numerical_assay.resource_names?.indexOf(resourceName)
    }
    if (resourceIndex === undefined || resourceIndex === -1) return

    // dispose existing assays from this dataset since we'll only display one resource at a time
    this.assayMap.get(dataSetId)?.forEach(mesh => {
      this.group.remove(mesh)
      disposeScene(mesh)
    })

    const numericalAssay = drillHoles.config[0].numerical_assay?.find(c => c.original.name === resourceName)
    if (!numericalAssay) return
    const grades = numericalAssay.grades
    const min = Math.min(...grades.map(grade => grade.min))
    const max = Math.max(...grades.map(grade => grade.max))

    // create a single geometry and material per grade to reduce draw calls
    const assayGeometries: Array<BufferGeometry[]> = grades.map(() => [])
    const gradeMaterials: MeshBasicMaterial[] = grades.map(grade => new MeshBasicMaterial({ color: grade.color }))

    for (let i = 0; i < drillHoles.data.length; i++) {
      const from_to = drillHoles.data[i].numerical_assay.from_to
      const resources = drillHoles.data[i].numerical_assay.resources
      const curve = this.surveyCurves.get(drillHoles.data[i].hole_id)
      if (!(from_to && resources && curve)) continue
      const depth = drillHoles.data[i].collar.depth
      for (let j = 0; j < from_to.from.length; j++) {
        const resource = resources[j][resourceIndex] // second index references resource type
        if (resource < min || resource > max) continue

        const gradeIndex = this._getGradeIndex(resource, grades)
        if (gradeIndex === -1) continue
        const geo = new CylinderGeometry(5, 5, from_to.to[j] - from_to.from[j], 5)

        const midPoint = (from_to.from[j] + from_to.to[j]) / 2
        const normalizedDepth = midPoint / depth

        const tangent = curve?.getTangentAt(normalizedDepth)!
        const axis = new Vector3(0, 1, 0)
        const quaternion = new Quaternion().setFromUnitVectors(axis.normalize(), tangent.normalize())
        const rotationMatrix = new Matrix4()
        rotationMatrix.makeRotationFromQuaternion(quaternion)
        geo.applyMatrix4(rotationMatrix)

        const position = curve?.getPointAt(normalizedDepth)
        if (!position) continue
        geo.translate(position.x, position.y, position.z)

        assayGeometries[gradeIndex].push(geo)
      }
    }

    const gradeMeshes = assayGeometries.map((geoArray, i) => {
      if (!geoArray.length) return new Mesh()
      const gradeGeo = BufferGeometryUtils.mergeGeometries(geoArray)
      const mesh = new Mesh(gradeGeo, gradeMaterials[i])
      this.group.add(mesh)
      return mesh
    })
    this.assayMap.set(dataSetId, gradeMeshes)
  }

  private _createAllDrillHoles() {
    this.cleanUp()

    for (const [dataSetId, drillHoles] of this.holes) {
      if (!drillHoles.localization || !drillHoles.data.length) continue

      const localizationOffset = new Vector3(
        drillHoles.localization.easting,
        drillHoles.localization.elevation,
        -drillHoles.localization.northing,
      )

      this._createAllCollars(drillHoles, localizationOffset)
      this._createAllSurveys(drillHoles, localizationOffset)
      this._createAllAssays(dataSetId) // create assays after surveys because they rely on the curves
    }
  }

  private async _getDrillHoleMap(drillDataSets: DrillDataSet[]): Promise<Map<number, DrillHoles>> {
    const drillTuples: [number, DrillHoles][] = (await Promise.all(
      drillDataSets.map(async dataSet => {
        try {
          //drill data
          const [drillHoles, drillConfig] = await Promise.all([
            $fetch<ApiListView<DrillHoleApiData>>(`/v2/drill/data/${dataSet.data_set_id}/hole_data`, {
              ...useFetchSharedOptions(),
              headers: {
                ...useFetchSharedOptions().headers,
                'X-Deck-Auth-UUID': `${this.loaderConfig.uuid}`,
              },
              params: { limit: 50000 }, // TODO: handle incremental loading
            }),
            $fetch<DrillDataConfig[]>(`/v2/drill/data/${dataSet.data_set_id}/configuration`, {
              ...useFetchSharedOptions(),
              headers: {
                ...useFetchSharedOptions().headers,
                'X-Deck-Auth-UUID': `${this.loaderConfig.uuid}`,
              },
            }),
          ])

          return [
            dataSet.data_set_id,
            {
              data: drillHoles.results,
              config: drillConfig,
              localization: dataSet.localization,
            },
          ]
        } catch (e) {
          return [dataSet.data_set_id, { data: [], config: [], localization: undefined }]
        }
      }),
    )) as [number, DrillHoles][]

    return new Map(drillTuples)
  }

  private _getGradeIndex(value: number, grades: Array<any>): number {
    const gradeIndex = grades.findIndex(grade => value <= grade.max && value >= grade.min)
    return gradeIndex
  }

  cleanUp() {
    this.group.clear()
    disposeScene(this.group)
  }
}
